import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { FC, useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { DeepPartial } from "redux";
import { Observable } from "rxjs";
import styled from "styled-components";
import { PrimaryButton } from "../../../components/button";
import { DateField } from "../../../components/forms/date";
import { TextField } from "../../../components/forms/text";
import { Toggle } from "../../../components/forms/toggle";
import { useFileLoader } from "../../../components/hooks/file-loader";
import { GridItem } from "../../../components/layout";
import { ResultWithProgress } from "../../../components/loader/models";
import { H2 } from "../../../components/typography";
import { Widget } from "../../../components/widget";
import { VerificationDTO } from "../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../store/verification/service";
import { mapObject } from "../../../utils/utils";
import { MerchantDueDiligenceRoutes } from "../routes";
import { Header } from "./header-form";

type RequestType = VerificationDTO.Requests.DueDiligence.Config.Post.T;
type ResponseType = VerificationDTO.Responses.DueDiligence.Config.Post.T;

const StyledWidget = styled(Widget)`
  height: auto;
  display: flex;
  flex-direction: column;
`;

export const DueDiligenceConfigPage: FC<{
  defaultValues: DeepPartial<RequestType>;
  onSubmit: (x: RequestType) => Observable<ResultWithProgress<any>>;
  image?: File;
  url?: string;
  enabledOptionalFields?: ResponseType["enabledOptionalFields"];
}> = ({
  defaultValues,
  onSubmit,
  children,
  image,
  url,
  enabledOptionalFields = [],
}) => {
    const {
      validation,
      labels,
    } = VerificationDTO.Requests.DueDiligence.Config.Post.useFields();

    const form = useForm<RequestType>({
      defaultValues,
      resolver: yupResolver(validation),
    });

    const [optionalFields, setOptionalFields] = useState<{
      WHITEPAPER: boolean;
      TOKENOMICS: boolean;
      COMPANY_MEMBERS: boolean;
    }>({
      COMPANY_MEMBERS: enabledOptionalFields.includes("COMPANY_MEMBERS"),
      TOKENOMICS: enabledOptionalFields.includes("TOKENOMICS"),
      WHITEPAPER: enabledOptionalFields.includes("WHITEPAPER"),
    });

    const router = useRouter();
    const loader = useFileLoader<ResponseType>();

    useEffect(() => {
      if (image) form.setValue("header.image", image);
    }, [image, form.setValue]);

    const handleSubmitCallback: (
      data: RequestType
    ) => Promise<void> = useCallback(
      async (data) => {
        try {
          const result = await loader(onSubmit(data));
          if (result.status === "done") {
            await Promise.all(
              mapObject(optionalFields, (val, key) =>
                val
                  ? VerificationService.enableDueDiligenceOptionalField(
                    result.data.id,
                    key
                  )
                  : VerificationService.disableDueDiligenceOptionalField(
                    result.data.id,
                    key
                  )
              )
            );

            router.stateService.go(
              MerchantDueDiligenceRoutes.names.single,
              {
                id: result.data.id,
              },
              {
                reload: true,
              }
            );
          }
        } catch (e: any) { }
      },
      [loader, onSubmit, router, optionalFields]
    );

    return (
      <form onSubmit={form.handleSubmit(handleSubmitCallback)}>
        <FormProvider {...form}>
          <StyledWidget>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <small>
                  <H2>Basic Info</H2>
                </small>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Controller
                  control={form.control}
                  name="emailAddress"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      {...field}
                      helperText={error?.message}
                      error={!!error}
                      label={labels.emailAddress}
                    />
                  )}
                />
                Company representative email
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Controller
                  control={form.control}
                  name="companyName"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={!!error}
                      fullWidth
                      helperText={error?.message}
                      type="text"
                      label={`company-name`}
                    />
                  )}
                />
                Full company name
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Controller
                  control={form.control}
                  name="expiresAt"
                  render={({ field, fieldState: { error } }) => (
                    <DateField
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      label={labels.expiresAt}
                    />
                  )}
                />
                This form is valid until the date above
              </Grid>
              {url ? (
                <>
                  <Grid item xs={12} md={9} sm={12} >
                    <a target="_blank" href={url}>
                      {url}
                    </a>
                  </Grid>
                  <Grid item xs={12} md={3} sm={12} >
                    <a href={url} target="_blank" style={{ width: "100%" }}>
                      <PrimaryButton fullWidth>Go to Form</PrimaryButton>
                    </a>
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12}>
                <small>
                  <H2>Optional Fields</H2>
                </small>
              </Grid>
              <Grid item xs={12}>
                <Toggle
                  onChange={(val) => {
                    setOptionalFields((x) => ({ ...x, WHITEPAPER: val }));
                  }}
                  value={optionalFields.WHITEPAPER}
                  label="Whitepaper"
                />
                <Toggle
                  onChange={(val) => {
                    setOptionalFields((x) => ({ ...x, TOKENOMICS: val }));
                  }}
                  value={optionalFields.TOKENOMICS}
                  label="Tokenomics"
                />
                <Toggle
                  onChange={(val) => {
                    setOptionalFields((x) => ({ ...x, COMPANY_MEMBERS: val }));
                  }}
                  value={optionalFields.COMPANY_MEMBERS}
                  label="Company members"
                />
              </Grid>
              <Grid item xs={12}>
                <small>
                  <H2>Header</H2>
                </small>
              </Grid>
              <Header />
              <Grid item xs={12}>
                <PrimaryButton type="submit">{children}</PrimaryButton>
              </Grid>
            </Grid>
          </StyledWidget>
        </FormProvider>
      </form>
    );
  };
